import React, { useState, useRef, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { ChatMessage, RecipeData } from '../../types';
import MarkdownRenderer from './MarkdownRenderer'; // 新增导入
import './styles.css';

interface AIChatPanelProps {
  onTableIdChange: (tableId: string) => void;
  onTableCellsChange: (tableId: string, changedCells: any[]) => void;
  onChatHistoryChange: (chatHistory: ChatMessage[]) => void;
  onDataRefresh: () => Promise<void>;
  chatHistory: ChatMessage[];
  currentRecipe: RecipeData | null;
  tableId: string | null; // 新增：接收当前tableId
  // 新增：通过 props 接收外部触发的消息
  externalMessage?: {
    content: string;
    options?: {
      rowId?: string;
      field?: string;
      cellContext?: any;
    };
    timestamp: number; // 用于触发检测
  } | null;
  aiUrl: string;
  apiKey: string;
}

// 错误边界组件
class MarkdownErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('MarkdownRenderer error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div style={{ color: '#666', fontStyle: 'italic' }}>
          内容渲染出错，显示原始文本
        </div>
      );
    }

    return this.props.children;
  }
}

const AIChatPanel: React.FC<AIChatPanelProps> = ({
  onTableIdChange,
  onTableCellsChange,
  onChatHistoryChange,
  onDataRefresh,
  chatHistory,
  currentRecipe,
  tableId, // 新增参数
  externalMessage, // 新增
  aiUrl,
  apiKey,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [restoringMessageId, setRestoringMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastExternalTimestamp = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true); // 添加挂载状态标记

  // 组件挂载状态管理
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 监听外部消息触发
  useEffect(() => {
    if (externalMessage && 
        externalMessage.timestamp > lastExternalTimestamp.current) {
      lastExternalTimestamp.current = externalMessage.timestamp;
      _sendMessageInternal(externalMessage.content, externalMessage.options);
    }
  }, [externalMessage]);

  // 预设建议问题
  const suggestions = [
    '帮我生成一个去油、温和、有玫瑰香味的洗脸皂配方',
    '分析当前配方的主要功能和益处',
    '检查配方合规性',
    '优化配方成本',
  ];

  // 滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 单独监控聊天历史变化
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, scrollToBottom]);

  // 接口请求函数
  const callAIAPI = async (
    userMessage: string, 
    options?: {
      rowId?: string;
      field?: string;
      version?: string;
      cellContext?: any;
    }
  ): Promise<any> => {
    // 优化：优先使用传入的tableId，其次使用currentRecipe的tableId
    const currentTableId = tableId || (currentRecipe ? currentRecipe.tableId : '');
    const userId = sessionStorage.getItem("agimx_user_id") || 'default_user';

    try {
      const response = await axios.post(aiUrl,
        {
          inputs:{
            table_id: currentTableId,
            question: userMessage,
            // 新增：传递单元格上下文信息
            ...(options?.rowId && { data_id: options.rowId }),
            ...(options?.field && { field: options.field }),
            ...(options?.version && { version: options.version }),
            // ...(options?.cellContext && { 
            //   cell_context: {
            //     column_name: options.cellContext.columnName,
            //     cell_value: options.cellContext.cellValue,
            //     row_index: options.cellContext.row,
            //     col_index: options.cellContext.col
            //   }
            // })
          },
          respond_mode: 'blocking',
          user: userId
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + apiKey,
          },
          timeout: 60000, // 60秒超时
        }
      );

      // axios 会自动解析 JSON，直接访问 data 属性
      const data = response.data?.data;
      console.log('AI接口响应数据:', data);
      const { outputs, status} = data;
      const {llm_text, table_id, version, items} = outputs;
      if(table_id){
        console.log('生成的表格ID:', outputs?.table_id, currentRecipe);
      }
      const responseData = {
        status: status,
        tableId: table_id,
        llmText: llm_text,
        version: version,
        changedCells: items
      }
      return responseData;
    } catch (error) {
      console.error('AI接口调用失败:', error);
      
      // 处理 axios 错误格式
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          // 服务器响应了错误状态码
          throw new Error(`接口请求失败: ${axiosError.response.status} ${axiosError.response.statusText}`);
        } else if (axiosError.request) {
          // 请求已发出但没有响应
          throw new Error('网络连接失败，请检查网络设置');
        }
      }
      
      // 其他未知错误
      throw new Error('请求处理失败，请稍后重试');
    }
  };

  // 新增：处理数据恢复
  const handleRestoreData = async (messageId: string,  version?: string) => {
    if (!version || restoringMessageId === messageId) return;
    
    setRestoringMessageId(messageId);
    
    // 构建恢复请求消息
    let restoreMessage = '请恢复上一次操作的数据状态';
    if (version) {
      // restoreMessage = `请恢复表格 ${operationContext.tableId} 的数据到之前的状态`;
      restoreMessage = `请恢复表格的数据到${version}版本`;
    }
    
    try {
      await _sendMessageInternal(restoreMessage, {
        version: version,
      });
    } catch (error) {
      console.error('恢复数据失败:', error);
    } finally {
      setRestoringMessageId(null);
    }
  };

  // 内部发送消息处理 - 重构为可复用的方法
  const _sendMessageInternal = async (
    message: string, 
    options?: {
      rowId?: string;
      fieldName?: string;
      cellContext?: any;
      version?: string;
    }
  ): Promise<void> => {
    if (!message.trim() || isTyping || !isMountedRef.current) return;

    const abortController = new AbortController();

    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: message.trim(),
      timestamp: new Date().toISOString(),
    };

    // 添加用户消息到历史记录
    const updatedHistory = [...chatHistory, newMessage];
    onChatHistoryChange(updatedHistory);
    setInputValue('');
    setIsTyping(true);

    try {
      // 调用AI接口，传递额外参数
      const aiResponseData = await callAIAPI(newMessage.content, options);

      // 检查组件是否仍然挂载
      if (!isMountedRef.current) return;
      
      // 临时使用模拟数据进行测试
      // const aiResponseData = {
      //   status: 'succeeded',
      //   tableId: 'intelligent_table_718b3846-9f04-4916-a59d-82ffccd70b0b',
      //   llmText: `本次请求为模拟数据测试`
      // };

      const { status: aiResponseStatus, tableId: responseTableId, llmText, version, changedCells } = aiResponseData;
      const aiResponseContent = llmText || (aiResponseStatus === 'succeeded' ? '配方数据已生成，请查看表格内容。' : '抱歉，我暂时无法处理您的请求。');
      
      const aiResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistana',
        content: aiResponseContent,
        timestamp: new Date().toISOString(),
        version: version,
        // 添加操作上下文信息
        operationContext: aiResponseStatus === 'succeeded' ? {
          tableId: responseTableId,
          operationType: message.includes('恢复') ? 'update' : 
                        message.includes('分析') ? 'analyze' : 'create',
          canRestore: true
        } : undefined
      };
      
      const finalHistory = [...updatedHistory, aiResponse];
      
      // 如果成功且有新的表格ID，更新表格数据
      if (aiResponseStatus === 'succeeded' && isMountedRef.current) {
        if(changedCells){
          // onTableIdChange(responseTableId);
          await onDataRefresh();
          console.log("刷新数据并更新单元格:",  changedCells,responseTableId);
          
          // 使用 requestAnimationFrame 确保DOM更新完成
          setTimeout(() => {
            onTableCellsChange(responseTableId, changedCells);
            console.log("单元格高亮应用完成");
          },1000);
        }else if(responseTableId){
          console.log('更新表格ID和数据:', responseTableId);
          onTableIdChange(responseTableId);
          await onDataRefresh();
        }
      }
      
      onChatHistoryChange(finalHistory);
      
    } catch (error) {
      console.error('发送消息失败:', error);
      
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      const errorResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistana',
        content: `抱歉，${errorMessage}。请稍后再试。`,
        timestamp: new Date().toISOString(),
      };
      
      const finalHistory = [...updatedHistory, errorResponse];
      onChatHistoryChange(finalHistory);
      
    } finally {
      setIsTyping(false);
    }
  };

  // 原有的处理发送消息方法 - 现在调用内部方法
  const handleSendMessage = async () => {
    await _sendMessageInternal(inputValue);
    // setInputValue('');
  };

  // 处理输入框按键
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 处理建议点击
  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  // 格式化时间
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('zh-CN', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="ai-chat-panel-container">
      {/* 聊天消息区域 */}
      <div className="ai-chat-messages">
        {chatHistory.length === 0 ? (
          <div className="ai-chat-empty">
            {/* <div className="ai-chat-empty-icon">🤖</div> */}
            <h3 className="ai-chat-empty-title">AI 配方助手</h3>
            <p className="ai-chat-empty-description">
              我是您的专属配方设计助手，可以帮您创建、分析和优化配方。
              您可以问我任何关于配方设计的问题！
            </p>
            <div className="ai-chat-empty-suggestions">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="ai-chat-empty-suggestion"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="ai-chat-message-list">
            {chatHistory.map((message) => (
              <div
                key={message.id}
                className={`ai-chat-message ${message.type}`}
              >
                {/* <div className={`ai-chat-avatar ${message.type}`}>
                  {message.type === 'user' ? '👤' : '🤖'}
                </div> */}
                <div className="ai-chat-content">
                  <div className={`ai-chat-bubble ${message.type}`}>
                    {/* 使用错误边界包裹 MarkdownRenderer */}
                    {message.type === 'assistana' ? (
                      <MarkdownErrorBoundary
                        fallback={
                          <div style={{ color: '#333', lineHeight: '1.5' }}>
                            {message.content}
                          </div>
                        }
                      >
                        <MarkdownRenderer 
                          content={message.content || ''}
                          className="ai-message-markdown"
                        />
                      </MarkdownErrorBoundary>
                    ) : (
                      message.content
                    )}
                  </div>
                  <div className="ai-chat-timestamp">
                    {formatTime(new Date(message.timestamp))}
                  </div>

                  {/* 新增：系统消息的恢复按钮 */}
                  {message.type === 'assistana' && message.version && (
                    <div className="ai-chat-actions">
                      <button
                        className="ai-chat-restore-btn"
                        onClick={() => handleRestoreData(message.id, message.version)}
                        disabled={restoringMessageId === message.id || isTyping}
                      >
                        {restoringMessageId === message.id ? '恢复中...' : '恢复本次数据'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* 正在输入指示器 */}
            {isTyping && (
              <div className="ai-chat-typing">
                {/* <div className="ai-chat-typing-avatar">🤖</div> */}
                <div className="ai-chat-typing-indicator">
                  <div className="ai-chat-typing-dots">
                    <div className="ai-chat-typing-dot"></div>
                    <div className="ai-chat-typing-dot"></div>
                    <div className="ai-chat-typing-dot"></div>
                  </div>
                  <span>AI正在思考...</span>
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* 输入区域 */}
      <div className="ai-chat-input-area">
        {/* 快捷建议 */}
        {chatHistory.length > 0 && (
          <div className="ai-chat-suggestions">
            {suggestions.slice(0, 3).map((suggestion, index) => (
              <button
                key={index}
                className="ai-chat-suggestion"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {/* 输入框和发送按钮 */}
        <div className="ai-chat-input-wrapper">
          <div className="ai-chat-input-container">
            <textarea
              ref={inputRef}
              className="ai-chat-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="输入您的问题或需求..."
              rows={1}
              disabled={isTyping}
            />
          </div>
          <button
            className="ai-chat-send-btn"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isTyping}
            title="发送消息 (Enter)"
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChatPanel;